import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import logo from "../../assets/logo2.png";
import {
  FaHome, FaHistory, FaCog, FaBell, FaShoppingBag,
  FaSignOutAlt, FaBars, FaUndo
} from "react-icons/fa";
import { ShoppingCart } from "lucide-react";
import { useCart } from "../../context/CartContext";

const NAV = [
  { label: "Overview", icon: FaHome, path: "/dashboard" },
  { label: "History", icon: FaHistory, path: "/dashboard?tab=history" },
  { label: "Services", icon: FaShoppingBag, path: "/dashboard?tab=services" },
  { label: "Notifications", icon: FaBell, path: "/dashboard?tab=notifications" },
  { label: "Settings", icon: FaCog, path: "/profile/edit" },
  { label: "Refund History", icon: FaUndo, path: "/withdraw/history" },
];

export default function CustomerLayout({ children, title, subtitle, action }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || "Patient";
  const initials = name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U";
  const walletBalance = parseFloat(user?.wallet_balance || 0);

  const handleLogout = () => { logout(); navigate("/login"); };

  const isActive = (path) => {
    const base = path.split("?")[0];
    return location.pathname === base;
  };

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex">
      {/* ── Sidebar ── */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[220px] bg-white border-r border-gray-100 flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:relative lg:flex`}>

        {/* Logo */}
        <div className="p-5 border-b border-gray-50 shrink-0">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center overflow-hidden shadow-sm border border-gray-100">
              <img src={logo} alt="UCH" className="w-6 h-6 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">UCH Self Service</p>
              <p className="text-gray-400 text-[10px]">University College Hospital</p>
            </div>
          </a>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(({ label, icon: Icon, path }) => (
            <button key={label} onClick={() => { navigate(path); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive(path) ? "bg-green-50 text-[#0d3d2e] font-semibold" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
              }`}>
              <Icon className={`w-4 h-4 ${isActive(path) ? "text-[#0d3d2e]" : "text-gray-400"}`} />
              {label}
            </button>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-gray-100 shrink-0">
          <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 cursor-pointer" onClick={() => navigate("/profile/edit")}>
            <div className="w-9 h-9 bg-[#0d3d2e] rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0">{initials}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
              <p className="text-xs text-green-700 font-semibold">₦{walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 mt-1 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition">
            <FaSignOutAlt className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <FaBars className="w-4 h-4 text-gray-600" />
            </button>
            <div>
              {title && <h1 className="font-bold text-gray-900 text-base leading-tight">{title}</h1>}
              {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {action}
            <button onClick={() => navigate("/wallet-pay")} className="relative p-2 text-gray-500 hover:text-[#0d3d2e] transition">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>
              )}
            </button>
            <button
              onClick={() => navigate("/profile/edit")}
              className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full hover:bg-gray-50 transition"
            >
              <div className="w-9 h-9 bg-[#0d3d2e] rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0">
                {initials}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-semibold text-gray-900 leading-tight">
                  {user?.username || name}
                </p>
                <p className="text-xs text-gray-400 leading-tight">
                  ₦{walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })} balance
                </p>
              </div>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
